import axios from "axios";

let accessToken = null;
let refreshPromise = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function clearAccessToken() {
  accessToken = null;
}

// ------------------------------------------------
// AXIOS INSTANCE
// ------------------------------------------------
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
  withCredentials: true,
});

// ------------------------------------------------
// JWT helpers
// ------------------------------------------------
function parseJwtExp(token) {
  try {
    const [, payload] = token.split(".");
    const norm = payload.replace(/-/g, "+").replace(/_/g, "/");
    const pad = "===".slice((norm.length + 3) % 4);
    const json = atob(norm + pad);
    const { exp } = JSON.parse(json);
    return typeof exp === "number" ? exp : 0;
  } catch {
    return 0;
  }
}

function isExpiringSoon(token, skewSec = 60) {
  if (!token) return true;
  const exp = parseJwtExp(token);
  const now = Math.floor(Date.now() / 1000);
  return exp === 0 || exp - now <= skewSec;
}

// ------------------------------------------------
// REFRESH
// ------------------------------------------------
async function doRefresh() {
  if (!refreshPromise) {
    refreshPromise = api
      .post("/api/auth/refresh", null, {
        _skipAuthRetry: true,
        headers: { Authorization: undefined },
      })
      .then((res) => {
        accessToken = res.data.access_token;
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// ------------------------------------------------
// REQUEST INTERCEPTOR
// ------------------------------------------------
api.interceptors.request.use(async (config) => {
  if (!config._skipAuthRetry) {
    if (isExpiringSoon(accessToken)) {
      try {
        await doRefresh();
      } catch {
        accessToken = null;
      }
    }
  }

  if (accessToken) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return config;
});

// ------------------------------------------------
// RESPONSE INTERCEPTOR
// ------------------------------------------------
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original._skipAuthRetry
    ) {
      original._retry = true;

      try {
        await doRefresh();
        return api(original);
      } catch {
        accessToken = null;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
